import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { BrutalButton } from "./Brutal";

/**
 * Confirmation dialog for cancelling a sale. Shared by Movement Log,
 * Receivables card, and Distributor Detail. The parent passes:
 *  - sale: { id, product_name, quantity, unit, amount? | sale_price? }
 *  - onClose(): close dialog without acting
 *  - onCancelled(result): success callback — parent should refresh
 *    its data and any revenue widget.
 */
export default function CancelSaleDialog({ sale, onClose, onCancelled }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  if (!sale) return null;

  const qty = sale.quantity || 0;
  const unit = sale.unit || "units";
  const product = sale.product_name || t("common.unknown");
  // Prefer explicit total amount; otherwise compute from sale_price.
  let amount = sale.amount;
  if (amount == null && sale.sale_price != null) {
    amount = Number(sale.sale_price) * qty;
  }
  const amountStr = amount != null ? `$${Number(amount).toFixed(2)}` : "—";

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sales/${sale.id}/cancel`);
      toast.success(
        t("cancel_sale.toast_success", {
          qty: data.quantity_returned,
          unit,
          product: data.product_name || product,
          amount: Number(data.refund_amount || 0).toFixed(2),
        })
      );
      onCancelled?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="cancel-sale-dialog"
      >
        <h2 className="text-xl font-semibold tracking-tight text-zinc-950">
          {t("cancel_sale.title")}
        </h2>
        <div
          className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-900 leading-relaxed"
          data-testid="cancel-sale-body"
        >
          {t("cancel_sale.body", {
            qty,
            unit,
            product,
            amount: amountStr,
          })}
        </div>
        <div className="flex gap-3 mt-5">
          <BrutalButton
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={onClose}
            disabled={busy}
            data-testid="cancel-sale-keep"
          >
            {t("cancel_sale.keep")}
          </BrutalButton>
          <BrutalButton
            type="button"
            className="flex-1 !bg-rose-600 hover:!bg-rose-700 !border-rose-700 text-white"
            onClick={submit}
            disabled={busy}
            data-testid="cancel-sale-confirm"
          >
            {busy ? t("cancel_sale.cancelling") : t("cancel_sale.confirm")}
          </BrutalButton>
        </div>
      </div>
    </div>
  );
}
